import type { PatientsSearchT } from "@/routes/_app/patients";
import type { DiaryViewT, StatefulDiaryT } from "@/types/database/diaries";
import type { Auth } from "@/utils/auth";
import { queryOptions } from "@tanstack/react-query";
import { validate as validateUUID } from "uuid";
import {
	getAllDiaries,
	getCountBySettings,
	getDiariesByPatient,
	getDiariesBySettings,
	getDiariesTabs,
	getDiaryById,
} from "./diaryApi";
import { diaryKeys } from "./diaryKeys";

// Add a timestamp to track when the last query was run
export const lastPatientsDiariesQueryTime = { value: 0 };

const defaultQueryConfig = {
	staleTime: 30000, // 30 seconds
	gcTime: 5 * 60 * 1000, // 5 minutes
	refetchOnWindowFocus: true,
	refetchOnMount: true,
	refetchOnReconnect: true,
	retry: 3,
	retryDelay: (attemptIndex: number) =>
		Math.min(1000 * 2 ** attemptIndex, 10000),
} as const;

export const allDiariesQueryOptions = () => {
	return queryOptions<DiaryViewT[]>({
		queryKey: diaryKeys.all,
		queryFn: () => getAllDiaries(),
		...defaultQueryConfig,
	});
};

export const diaryByIdQueryOptions = (diaryId: string) => {
	if (!validateUUID(diaryId)) {
		return queryOptions<StatefulDiaryT | null>({
			queryKey: diaryKeys.detail(diaryId),
			queryFn: () => Promise.resolve(null),
			...defaultQueryConfig,
		});
	}

	return queryOptions<StatefulDiaryT | null>({
		queryKey: diaryKeys.detail(diaryId),
		queryFn: () => getDiaryById(diaryId),
		...defaultQueryConfig,
	});
};

export const diariesByPatientQueryOptions = (patientId: string) => {
	if (!validateUUID(patientId)) {
		return queryOptions<StatefulDiaryT[]>({
			queryKey: diaryKeys.byPatient(patientId),
			queryFn: () => Promise.resolve([]),
			...defaultQueryConfig,
		});
	}

	return queryOptions<StatefulDiaryT[]>({
		queryKey: diaryKeys.byPatient(patientId),
		queryFn: () => getDiariesByPatient(patientId),
		...defaultQueryConfig,
	});
};

export const diariesTabsQueryOptions = (
	filters: Record<string, unknown> = {},
) => {
	return queryOptions<{ tab: string | null }[]>({
		queryKey: diaryKeys.tabs(filters),
		queryFn: () => getDiariesTabs(filters),
		...defaultQueryConfig,
	});
};

export const diariesBySettingsQueryOptions = (
	settings: PatientsSearchT,
	auth: Auth,
) => {
	return queryOptions<DiaryViewT[]>({
		queryKey: diaryKeys.bySettings(settings),
		queryFn: () => getDiariesBySettings(settings, auth),
		...defaultQueryConfig,
		enabled: !!settings.tab,
	});
};

export const countBySettingsQueryOptions = (
	settings: PatientsSearchT,
	auth: Auth,
) => {
	return queryOptions({
		queryKey: diaryKeys.count(settings),
		queryFn: () => getCountBySettings(settings, auth),
		...defaultQueryConfig,
		enabled: !!settings.tab,
	});
};
